// src/components/common/Navigation.jsx
import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  ShoppingCart,
  Clock,
  LayoutDashboard,
  Package,
  Users,
  Store,
  Tag,
  ArrowLeftRight,
  ShoppingBag,
  Settings,
  UserCheck,
  BarChart2,
  ArrowUpCircle,
  Boxes,
  History,
  Wallet
} from 'lucide-react';

import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';

const Navigation = () => {
  const { language, currentUser } = useApp();
  // 同样的兜底：如果 language 是一个 translations.js 里还没有翻译内容的代码
  // （比如新加的马来语/繁体中文），不要让整个导航栏崩溃，退回中文
  const t = translations[language] || translations['zh'] || {};

  const navItems = [
    {
      path: '/pos',
      icon: ShoppingCart,
      label: t.pos || '收银台',
      roles: ['admin', 'manager', 'cashier']
    },
    {
      path: '/shift',
      icon: Clock,
      label: t.shift || '班次管理',
      roles: ['admin', 'manager', 'cashier']
    },
    {
      path: '/dashboard',
      icon: LayoutDashboard,
      label: t.dashboard || '数据看板',
      roles: ['admin', 'manager']
    },
    {
      path: '/sales-report',
      icon: BarChart2,
      // 之前这里只判断了en/km两种，新加的马来语/繁体中文会漏到中文兜底——
      // 现在统一走 translations.js 里补的这个key，覆盖全部5种语言
      label: t.salesReportNav || '销售报表',
      roles: ['admin', 'manager']
    },
    {
      path: '/products',
      icon: Package,
      label: t.products || '商品管理',
      roles: ['admin', 'manager']
    },
    {
      path: '/inventory',
      icon: Boxes,
      label: t.inventoryManagementNav || '库存管理',
      roles: ['admin', 'manager']
    },
    {
      path: '/inventory-logs',
      icon: History,
      label: t.inventoryLogsNav || '库存变动记录',
      roles: ['admin', 'manager']
    },
    {
      path: '/categories',
      icon: Tag,
      label: t.categories || '分类管理',
      roles: ['admin']
    },
    {
      path: '/employees',
      icon: Users,
      label: t.employees || '员工管理',
      roles: ['admin', 'manager']
    },
    {
      path: '/stores',
      icon: Store,
      // 复用之前给"门店管理"页面标题加的同一个key，不用重复造一个新的
      label: t.storesTitle || '门店管理',
      roles: ['admin'],
      store_type: 'headquarters'
    },
    {
      path: '/stock-transfers',
      icon: ArrowLeftRight,
      label: t.stockTransfersNav || '库存划拨',
      roles: ['admin', 'manager']
    },
    {
      path: '/online-orders',
      icon: ShoppingBag,
      label: t.onlineOrdersNav || '线上订单',
      roles: ['admin', 'manager']
    },
    {
      path: '/customers',
      icon: UserCheck,
      label: t.customerManagementNav || '客户管理',
      roles: ['admin', 'manager']
    },
    {
      path: '/credit-customers',
      icon: Wallet,
      label: t.creditCustomersNav || '记账客户管理',
      roles: ['admin', 'manager']
    },
    {
      path: '/shop-settings',
      icon: Settings,
      label: t.shopSettingsNav || '商家设置',
      roles: ['admin'],
      store_type: 'headquarters'
    },
    {
      path: '/usdt-withdraw',
      icon: ArrowUpCircle,
      label: t.usdtWithdrawNav || 'USDT提币审核',
      roles: ['admin'],
      store_type: 'headquarters'
    }
  ];

  const visibleNavItems = navItems.filter(item =>
    item.roles.includes(currentUser?.role) &&
    (!item.store_type || item.store_type === currentUser?.store_type)
  );

  return (
    <nav className="bg-white shadow-md">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex space-x-1 overflow-x-auto">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  flex items-center px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap
                  ${isActive
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }
                `}
              >
                <Icon className="w-5 h-5 mr-2" />
                {item.label}
              </NavLink>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default Navigation;